// src/pages/BgpStatusPage.tsx (o la ruta que uses)
import React, { useEffect, useState, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { GenericTable } from "@ui-components/Table/GenericTable";
import { BgpStatus, statusColumns } from "./BgpStatusData";
import { getNeighborsStatus } from "./request/request";
import { useAppSelector } from "@redux/hooks";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { makeBgpStatusFields } from "./bgpStatusFieldSpec";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";


interface RawNeighborItem {
	neighbor: string;
	as: number;
	local_as: number;
	peer_as: number;
	up: string;
	state: string;
	ctxr: string;
	ctxr_description: string;
	in: number;
	out: number;
	description?: string | null;
}

export function BgpStatusPage() {
	const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
	const { tenantId } = useAppSelector(state => state.selection);

	const [isFetching, setIsFetching] = useState(false);
	const [neighborData, setNeighborData] = useState<BgpStatus[]>([]);

	// Filtros con GenericSearchbar
	const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
	const bgpStatusFields = useMemo(() => makeBgpStatusFields(), []);

	// Generate suggested keys from field labels
	const suggestedKeys = useMemo(() => {
		return bgpStatusFields.map((f) => f.label);
	}, [bgpStatusFields]);

	// 1) Encapsulamos la llamada GET en una función para poder reusarla (useEffect + Refresh)
	const fetchNeighbors = async () => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const resp = await getNeighborsStatus(token, tenantId);

			// 'resp.data' es un array con elementos del tipo 'RawNeighborItem'
			const shaped: BgpStatus[] = (resp.data as RawNeighborItem[]).map(
				(item) => ({
					ctxr: item.ctxr ?? "",
					ctxr_description: item.ctxr_description ?? "",
					neighbor: item.neighbor ?? "",
					description: item.description ?? "",
					state: item.state ?? "",
					up: item.up ?? "",
					local_as: item.local_as ?? "",
					peer_as: item.peer_as ?? "",
					in: item.in || 0,
					out: item.out || 0,
				})
			);
			setNeighborData(shaped);
		} catch (err) {
			console.error("Error fetching neighbors =>", err);
		} finally {
			setIsFetching(false);
		}
	};

	// 2) Llamamos fetchNeighbors al montar (si está logueado)
	useEffect(() => {
		if (isLoading) return;

		if (!isAuthenticated) {
			console.warn("User not authenticated; skipping fetch.");
			return;
		}

		if (tenantId === 0) {
			console.log("No tenantId seleccionado todavía; no hacemos fetch...");
			return;
		}

		fetchNeighbors();
	}, [
		isLoading,
		isAuthenticated,
		getAccessTokenSilently,
		tenantId,
	]);

	// Build maps for filter normalization
	const fieldById = useMemo(() => {
		const m = new Map<string, FilterField<BgpStatus>>();
		for (const f of bgpStatusFields) {
			const id = (f.key ?? labelId(f.label)).toLowerCase();
			m.set(id, f);
		}
		return m;
	}, [bgpStatusFields]);
	const keyMap = useMemo(() => {
		const m = new Map<string, string>();
		for (const f of bgpStatusFields) {
			const id = (f.key ?? labelId(f.label)).toLowerCase();
			const label = f.label.toLowerCase();
			m.set(id, id);
			m.set(label, id);
		}
		return m;
	}, [bgpStatusFields]);

	// Normalize filters
	const normalizedFilters = useMemo(() => {
		return activeFilters.map((f) => {
			const token = f.op
				? { ...f }
				: parseToken(f.key ? `${f.key}=${f.value}` : f.value);

			if (token.key) {
				const k = token.key.trim().toLowerCase();
				token.key = keyMap.get(k) ?? labelId(token.key);
			}

			const field = token.key ? fieldById.get(token.key) : undefined;
			if (field?.type === "string") {
				const quoted = token.value.match(/^"(.*)"$/);
				if (quoted) {
					token.value = quoted[1];
					token.op = "=";
				} else {
					token.op = ":";
				}
			}
			return token;
		});
	}, [activeFilters, keyMap, fieldById]);

	// Build predicate using advancedFilters system
	const predicate = useMemo(
		() => buildPredicate<BgpStatus>(normalizedFilters, bgpStatusFields),
		[normalizedFilters, bgpStatusFields]
	);

	// Apply predicate to filter data
	const filteredData = useMemo(
		() => neighborData.filter(predicate),
		[neighborData, predicate]
	);

	return (
		<div className="relative">
			{/* Barra superior con el filtro y el botón Refresh */}
			<GenericCard
			isCollapsible={false}
			isLoading={isLoading}
			bodyContent={
			<>
				<div className="flex items-center">
					<div className="w-full max-w-full sm:max-w-[500px] my-5">
						<GenericSearchbar
							placeholder="Find by attribute or value (e.g., CTX-R: CTX-R-f20d18)"
							suggestedKeys={suggestedKeys}
							onFiltersChange={setActiveFilters}
							liveFiltering={true}
							debounceMs={200}
						/>
					</div>
				</div>

				{/* Tabla */}
				{isFetching ? (
					<div className="p-4">
					<SkeletonTableRows
						rows={5}
						cols={5}
						colWidths={["200px","150px"]}
						rowHeight={15}
						rowGapClassName="space-y-3"
					/>
					</div>
				) : (
					<GenericTable
						columns={statusColumns}
						data={filteredData}
						initialRowsPerPage={10}
						rowsPerPageOptions={[5, 10]}
						width="100%"
						tableClassName="bg-semantic-bg-primary"
					/>
				)}
				</>
			}
		/>
		</div>
	);
}

export default BgpStatusPage;
